import React, { useEffect, useRef, useState } from "react";
import { Sliders, ChevronDown, ChevronUp, Zap, Play, Pause, Music2, Mic, Plus, X } from "lucide-react";
import MixerChannel from "./MixerChannel";
import { FX_LABELS, FX_KEYS } from "../lib/audioFxPresets";

/**
 * DirectorMixerRack — HORIZONTAL bottom-docked audio rack (iter30).
 *
 * Lives below the program preview (full page width) so it can fit 8–10
 * channels + an integrated Music strip + Announcer FX strip without going
 * off-screen. Still collapsible to a 40px handle, and still detachable into
 * a floating window for dual-monitor workflows.
 *
 * Channels build out of DEFAULTS + dynamic `guest_{id}` entries. Master VU is
 * wired to a real AnalyserNode on the broadcaster's outbound stream.
 *
 * Music + Announcer are driven via the `broadcasterProxy` prop (the one
 * AdvancedDirectorPanel publishes via `onBroadcasterReady`) so creators can
 * pick tracks, hit Play/Pause, and change the voice FX WITHOUT leaving the
 * rack.
 */

const DEFAULTS = {
  mic:    { label: "Mic",     color: "sky",     volume: 1.0, muted: false, soloed: false, eq: { low: 0, mid: 0, high: 0 }, fx: "off", source: "camera_1" },
  mic2:   { label: "Mic 2",   color: "sky",     volume: 1.0, muted: true,  soloed: false, eq: { low: 0, mid: 0, high: 0 }, fx: "off", source: "camera_2" },
  music:  { label: "Music",   color: "violet",  volume: 0.55, muted: false, soloed: false, eq: { low: 0, mid: 0, high: 0 }, source: "music" },
  sfx:    { label: "SFX",     color: "amber",   volume: 0.8, muted: false, soloed: false, eq: { low: 0, mid: 0, high: 0 }, source: "ad" },
  master: { label: "Master",  color: "yellow",  volume: 1.0, muted: false, soloed: false, eq: { low: 0, mid: 0, high: 0 }, source: "auto" },
};

// Source assignment options — used by the tiny per-channel dropdown so the
// director can lock a fader to a specific input (otherwise stays on "auto").
const TRACK_SOURCES = [
  { key: "auto", label: "Auto" },
  { key: "camera_1", label: "Cam 1" },
  { key: "camera_2", label: "Cam 2" },
  { key: "camera_3", label: "Cam 3" },
  { key: "music", label: "Music" },
  { key: "ad", label: "Ad" },
];

// 11 empty guest slots always render so the rack always shows 16 channels
// (Mic · Mic 2 · Music · SFX · G1-G11 · Master). When real guests arrive
// they fill these placeholders in order; beyond 11 guests the rack auto-
// populates extra channels.
const PLACEHOLDER_GUESTS = 11;

// Ensure display order: mic → mic2 → music → sfx → guest_… → master last.
function reorderForDisplay(map) {
  const fixedOrder = ["mic", "mic2", "music", "sfx"];
  const guestKeys = Object.keys(map).filter((k) => k.startsWith("guest_"));
  const out = {};
  fixedOrder.forEach((k) => { if (map[k]) out[k] = map[k]; });
  guestKeys.forEach((k) => { out[k] = map[k]; });
  if (map.master) out.master = map.master;
  return out;
}

const MIC_FX = FX_KEYS.map((k) => ({ key: k, label: FX_LABELS[k] }));

export const ANNOUNCER_PRESETS = [
  { key: "off", label: "Clean" },
  { key: "podcast", label: "Podcast" },
  { key: "radio", label: "Radio" },
  { key: "stadium", label: "Stadium" },
  { key: "telephone", label: "Phone" },
  { key: "robot", label: "Robot" },
  { key: "echo", label: "Echo" },
  { key: "reverb", label: "Reverb" },
];

export default function DirectorMixerRack({
  open = true,
  onToggle,
  audioCtx,
  outputDest,
  guests = [],
  floating = false,
  onToggleFloat,
  broadcasterProxy,
}) {
  const [channels, setChannels] = useState(DEFAULTS);
  const [masterLevel, setMasterLevel] = useState(0);

  // Sync guest channels — keep PLACEHOLDER_GUESTS empty slots visible, fill
  // them with real guests in order, overflow beyond that auto-populates.
  useEffect(() => {
    setChannels((prev) => {
      const next = {};
      // Retain non-guest channels
      Object.keys(prev).forEach((k) => {
        if (!k.startsWith("guest_")) next[k] = prev[k];
      });
      const realCount = (guests || []).length;
      const totalGuestSlots = Math.max(PLACEHOLDER_GUESTS, realCount);
      for (let i = 0; i < totalGuestSlots; i++) {
        const g = guests?.[i];
        const key = g ? `guest_${g.id}` : `guest_slot_${i}`;
        const existing = prev[key] || prev[`guest_slot_${i}`];
        next[key] = existing
          ? { ...existing, label: g ? (g.user_name || `Guest ${i + 1}`).slice(0, 10) : `G${i + 1}` }
          : {
              label: g ? (g.user_name || `Guest ${i + 1}`).slice(0, 10) : `G${i + 1}`,
              color: ["rose", "sky", "amber", "violet", "emerald"][i % 5],
              volume: 1.0, muted: !g, soloed: false,
              eq: { low: 0, mid: 0, high: 0 }, fx: "off",
              placeholder: !g,
            };
      }
      return reorderForDisplay(next);
    });
  }, [guests]);

  // Real master VU (AnalyserNode on the outbound stream)
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  useEffect(() => {
    if (!audioCtx || !outputDest) return;
    try {
      const src = audioCtx.createMediaStreamSource(outputDest.stream);
      const an = audioCtx.createAnalyser();
      an.fftSize = 1024; an.smoothingTimeConstant = 0.7;
      src.connect(an);
      analyserRef.current = an;
    } catch {}
    const buf = new Uint8Array(1024);
    const tick = () => {
      if (!analyserRef.current) return;
      analyserRef.current.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      setMasterLevel(Math.min(1, Math.sqrt(sum / buf.length) * 2.5));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [audioCtx, outputDest]);

  // Simulated levels for non-master channels so VUs feel alive
  const [simT, setSimT] = useState(0);
  useEffect(() => {
    if (!open) return;
    const h = setInterval(() => setSimT((t) => t + 0.1), 120);
    return () => clearInterval(h);
  }, [open]);
  const simLevel = (phase) => {
    const base = 0.32 + 0.26 * Math.sin(simT * 0.7 + phase);
    return Math.max(0.05, Math.min(0.95, base + (Math.random() - 0.5) * 0.08));
  };

  const update = (key, patch) => {
    setChannels((c) => ({ ...c, [key]: { ...c[key], ...patch } }));
    // Live-wire the volume / FX changes to the real audio graph so the
    // rack actually changes what the viewers hear, not just the UI.
    if (!broadcasterProxy) return;
    if (patch.volume !== undefined) {
      const vol = Math.max(0, Math.min(1.5, patch.volume));
      broadcasterProxy.setChannelVol?.(key, vol);
      // Backwards-compat: also call the named setters for legacy proxies
      // that haven't been upgraded to setChannelVol.
      if (key === "music") broadcasterProxy.setMusicVol?.(vol);
      else if (key === "master") broadcasterProxy.setMasterVol?.(vol);
      else if (key === "sfx") broadcasterProxy.setSfxVol?.(vol);
      else if (key === "mic") broadcasterProxy.setMicVol?.(vol);
      else if (key === "mic2") broadcasterProxy.setMic2Vol?.(vol);
    }
    if (patch.muted !== undefined) {
      // Mute = drive the channel gain to 0 (or back to its slider value)
      const restoreVol = (v) => Math.max(0, Math.min(1.5, v ?? 1));
      const v = restoreVol(channels[key]?.volume);
      const nextVol = patch.muted ? 0 : v;
      broadcasterProxy.setChannelVol?.(key, nextVol);
      if (key === "music") broadcasterProxy.setMusicVol?.(nextVol);
      else if (key === "master") broadcasterProxy.setMasterVol?.(nextVol);
      else if (key === "sfx") broadcasterProxy.setSfxVol?.(nextVol);
      else if (key === "mic") broadcasterProxy.setMicVol?.(nextVol);
      else if (key === "mic2") broadcasterProxy.setMic2Vol?.(nextVol);
    }
    if (patch.fx !== undefined) {
      // Apply the FX chain on the real audio graph. Works for mic, mic2,
      // music, sfx — guests are passthrough today (placeholder slots) so
      // those calls are no-ops, but the wiring is ready for when guest
      // audio gets its own MediaStreamSource.
      broadcasterProxy.setChannelFx?.(key, patch.fx);
    }
  };

  const channelKeys = Object.keys(channels);
  const levels = {};
  channelKeys.forEach((k, i) => {
    if (channels[k].muted) { levels[k] = 0; return; }
    levels[k] = k === "master" ? masterLevel : simLevel(i * 0.9);
  });
  const anySolo = Object.values(channels).some((c) => c.soloed);
  const effectiveMuted = (k) => channels[k].muted || (anySolo && !channels[k].soloed);

  // Master FX pads
  const [frozen, setFrozen] = useState(false);
  const [duck, setDuck] = useState(false);

  // --- Local mirror of broadcaster's music/announcer state ---
  const [musicState, setMusicState] = useState({ track: null, musicPlaying: false, musicVol: 0.55, announcer: "off" });
  useEffect(() => {
    if (!broadcasterProxy) return;
    const h = setInterval(() => {
      const s = broadcasterProxy.getState?.();
      if (s) setMusicState(s);
    }, 400);
    return () => clearInterval(h);
  }, [broadcasterProxy]);

  // ---- Collapsed (bottom handle) state ----
  if (!open) {
    return (
      <div data-testid="mixer-rack-collapsed" className="shrink-0 h-10 bg-[#05080F] border-t border-tv-accent/30 flex items-center justify-center gap-3 px-4">
        <button
          data-testid="mixer-rack-open-btn"
          onClick={onToggle}
          className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-tv-accent/15 hover:bg-tv-accent/30 text-tv-accent font-black text-xs uppercase tracking-[0.2em]"
          title="Open audio mixer rack"
        >
          <ChevronUp className="w-4 h-4" />
          <Sliders className="w-4 h-4" />
          Audio Rack
        </button>
      </div>
    );
  }

  return (
    <aside
      data-testid="director-mixer-rack"
      className={`bg-[#05080F] border-tv-accent/30 flex flex-col relative overflow-hidden ${
        floating
          ? "fixed bottom-6 left-1/2 -translate-x-1/2 w-[min(92vw,1200px)] max-h-[70vh] rounded-2xl border-2 shadow-[0_20px_60px_rgba(0,0,0,0.8)] z-[55]"
          : "shrink-0 w-full border-t"
      }`}
      style={{
        backgroundImage:
          "linear-gradient(90deg, rgba(255,212,0,0.04) 0%, transparent 50%), " +
          "repeating-linear-gradient(0deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 1px, transparent 1px, transparent 32px), " +
          "repeating-linear-gradient(90deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 1px, transparent 1px, transparent 32px)",
      }}
    >
      {/* Neon edge */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-tv-accent to-transparent opacity-60" />

      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-white/10 bg-black/40 z-10">
        <Sliders className="w-4 h-4 text-tv-accent" />
        <div className="font-display font-black text-xs tracking-wider text-white">
          Audio Rack <span className="text-tv-accent">By Tap In TV</span>
        </div>
        <div className="flex-1" />

        {/* Mic 2 picker — host can plug in a second USB mic mid-broadcast
            and route it through the rack with its own fader + FX. */}
        <Mic2Picker broadcasterProxy={broadcasterProxy} />

        {/* Master FX pads — kept inline in the header so they're always in reach */}
        <button
          data-testid="mixer-rack-freeze"
          onClick={() => setFrozen((v) => !v)}
          aria-pressed={frozen}
          className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded border transition ${frozen ? "bg-cyan-500/30 border-cyan-400 text-cyan-200" : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"}`}
        >
          Freeze
        </button>
        <button
          data-testid="mixer-rack-duck"
          onClick={() => setDuck((v) => !v)}
          aria-pressed={duck}
          className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded border transition ${duck ? "bg-amber-500/25 border-amber-400 text-amber-200" : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"}`}
        >
          Duck
        </button>
        <button
          data-testid="mixer-rack-panic"
          onClick={() =>
            setChannels((c) => {
              const next = {};
              Object.keys(c).forEach((k) => (next[k] = { ...c[k], muted: true, soloed: false }));
              return next;
            })
          }
          className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded border border-red-500/60 bg-red-500/15 text-red-200 hover:bg-red-500/30"
        >
          Panic
        </button>
      </div>

      {/* Channel strips — horizontal scroll with generous spacing so tracks
          never feel cramped. Each channel has a fixed minimum width (96px)
          so faders / EQ / FX dropdowns stay readable on every screen. */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden px-3 py-2 mixer-scroll">
        <div className="flex gap-3 items-stretch min-w-min">
          {channelKeys.map((k) => (
            <div key={k} className="shrink-0 w-[96px] flex flex-col items-center gap-1.5">
              <MixerChannel
                testidPrefix={`mixer-rack-${k}`}
                label={channels[k].label}
                color={channels[k].color}
                master={k === "master"}
                level={levels[k]}
                volume={effectiveMuted(k) ? 0 : channels[k].volume}
                onVolumeChange={(v) => update(k, { volume: v, muted: false })}
                muted={channels[k].muted}
                onMuteToggle={() => update(k, { muted: !channels[k].muted })}
                soloed={channels[k].soloed}
                onSoloToggle={() => update(k, { soloed: !channels[k].soloed })}
                eq={channels[k].eq}
                onEqChange={(next) => update(k, { eq: next })}
              />
              {(k === "mic" || k === "mic2" || k === "music" || k === "sfx" || k.startsWith("guest_")) && (
                <select
                  data-testid={`mixer-rack-${k}-fx`}
                  value={channels[k].fx || "off"}
                  onChange={(e) => update(k, { fx: e.target.value })}
                  className="text-[10px] bg-black/60 border border-white/10 rounded px-1.5 py-1 text-white w-full focus:border-tv-accent outline-none"
                  title={`${channels[k].label} voice FX — applied in real time`}
                >
                  {MIC_FX.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
              )}
              {/* Track source assignment — Auto / Cam 1-3 / Music / Ad — so the
                  director can lock any fader to a specific input. */}
              {k !== "master" && (
                <select
                  data-testid={`mixer-rack-${k}-source`}
                  value={channels[k].source || "auto"}
                  onChange={(e) => update(k, { source: e.target.value })}
                  className="text-[10px] bg-black/40 border border-white/10 rounded px-1.5 py-1 text-tv-accent w-full focus:border-tv-accent outline-none"
                  title={`${channels[k].label} → route source`}
                >
                  {TRACK_SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              )}
            </div>
          ))}
        </div>
        <style>{`
          .mixer-scroll { scrollbar-width: thin; scrollbar-color: rgba(255,212,0,0.35) transparent; }
          .mixer-scroll::-webkit-scrollbar { height: 8px; }
          .mixer-scroll::-webkit-scrollbar-track { background: transparent; }
          .mixer-scroll::-webkit-scrollbar-thumb { background: rgba(255,212,0,0.35); border-radius: 6px; }
          .mixer-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,212,0,0.6); }
        `}</style>
      </div>
    </aside>
  );
}


/**
 * Mic2Picker — tiny header dropdown that lets the host plug in a second
 * audio input (USB mic, headset, virtual cable) WITHOUT restarting the
 * broadcast. Once added, mic2 shows up in the rack as its own channel
 * with fader + FX so the host can blend a second voice in real time.
 */
function Mic2Picker({ broadcasterProxy }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(false);
  const [devices, setDevices] = useState([]);
  const [busy, setBusy] = useState(false);

  // Probe inputs whenever the picker is opened — gives fresh USB mic
  // results even if the host plugged in mid-stream.
  useEffect(() => {
    if (!open || !broadcasterProxy) return;
    let alive = true;
    (async () => {
      try {
        // Trigger the permissions prompt once so labels show up properly.
        try { await navigator.mediaDevices.getUserMedia({ audio: true }).then((s) => s.getTracks().forEach((t) => t.stop())); } catch { /* user may have denied */ }
        const all = await navigator.mediaDevices.enumerateDevices();
        if (!alive) return;
        const audioIn = all.filter((d) => d.kind === "audioinput");
        setDevices(audioIn);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [open, broadcasterProxy]);

  // Reflect mic2 active-state from the proxy when known.
  useEffect(() => {
    const s = broadcasterProxy?.getMic2State?.();
    if (s) setActive(!!s.active);
  }, [broadcasterProxy, open]);

  const pick = async (deviceId) => {
    if (!broadcasterProxy?.addMic2) return;
    setBusy(true);
    const s = await broadcasterProxy.addMic2(deviceId);
    setBusy(false);
    if (s) { setActive(true); setOpen(false); }
  };

  const remove = () => {
    broadcasterProxy?.removeMic2?.();
    setActive(false); setOpen(false);
  };

  if (!broadcasterProxy) return null; // proxy not ready yet

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="mixer-rack-mic2-picker-btn"
        onClick={() => setOpen((o) => !o)}
        className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded border inline-flex items-center gap-1 transition ${
          active
            ? "bg-emerald-500/25 border-emerald-400 text-emerald-100"
            : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
        }`}
        title={active ? "Mic 2 is live — click to swap or remove" : "Plug in a second mic"}
      >
        {active ? <Mic className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
        Mic 2{active ? "" : "+"}
      </button>
      {open && (
        <div data-testid="mixer-rack-mic2-picker-menu" className="absolute right-0 top-full mt-1 w-72 bg-[#0B0F16] border border-white/10 rounded-lg shadow-2xl z-50 p-2 max-h-72 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-wider font-black text-white/65 px-1 pb-1">Pick an audio input</div>
          {devices.length === 0 && (
            <div className="text-[11px] text-white/45 px-1 py-2">No audio inputs detected. Plug in a mic and re-open this menu.</div>
          )}
          {devices.map((d, i) => (
            <button
              key={d.deviceId || i}
              type="button"
              disabled={busy}
              onClick={() => pick(d.deviceId)}
              data-testid={`mixer-rack-mic2-pick-${i}`}
              className="w-full text-left text-[11px] text-white/85 hover:bg-white/5 rounded px-2 py-1.5 disabled:opacity-50 truncate"
            >
              {d.label || `Audio input ${i + 1}`}
            </button>
          ))}
          {active && (
            <button
              type="button"
              onClick={remove}
              data-testid="mixer-rack-mic2-remove"
              className="mt-1 w-full text-[10px] uppercase tracking-wider font-black text-rose-300 hover:bg-rose-500/15 rounded px-2 py-1.5 inline-flex items-center justify-center gap-1"
            ><X className="w-3 h-3" /> Remove Mic 2</button>
          )}
        </div>
      )}
    </div>
  );
}
